import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import type { PlotDiff } from '../../../types/reimportTypes';
import ReimportPlotRow from './ReimportPlotRow';

interface ReimportDiffTableProps {
  plots: PlotDiff[];
}

const ReimportDiffTable: React.FC<ReimportDiffTableProps> = ({ plots }) => {
  const { filterText, setFilterText } = useSiteReimportStore();
  const [showUnchanged, setShowUnchanged] = React.useState(false);

  const filteredPlots = useMemo(() => {
    let result = plots;

    if (!showUnchanged) {
      result = result.filter(p => p.changeType !== 'Unchanged');
    }

    if (filterText.trim()) {
      const search = filterText.toLowerCase();
      result = result.filter(
        p =>
          p.plotNumber.toLowerCase().includes(search) ||
          p.plotName.toLowerCase().includes(search)
      );
    }

    return result;
  }, [plots, showUnchanged, filterText]);

  const unchangedCount = plots.filter(p => p.changeType === 'Unchanged').length;

  return (
    <Paper sx={{ p: 2 }} data-testid="reimport-diff-table">
      <Typography variant="h6" gutterBottom>
        Detailed Changes
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          placeholder="Filter by plot..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
          data-testid="reimport-diff-filter"
        />

        {unchangedCount > 0 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showUnchanged}
                onChange={(_e, checked) => setShowUnchanged(checked)}
                data-testid="reimport-show-unchanged-toggle"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Show unchanged ({unchangedCount})
              </Typography>
            }
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredPlots.length} of {plots.length} plots
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ width: 100 }}>Change</TableCell>
              <TableCell>Plot No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Labour Items</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center" sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlots.map(plot => (
              <ReimportPlotRow
                key={plot.plotNumber}
                plot={plot}
                showUnchangedItems={showUnchanged}
              />
            ))}
            {filteredPlots.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No plots match the current filter
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ReimportDiffTable;
